
import { supabase } from '@/integrations/supabase/client';

export interface Report {
  id: string;
  post_id: string;
  reporter_id: string;
  reason: string;
  description: string | null;
  status: string;
  created_at: string;
  updated_at: string;
  reviewed_by: string | null;
  reviewed_at: string | null;
  moderator_notes: string | null;
  reporter_name: string | null;
  post_caption: string | null;
  pet_name: string | null;
}

export interface AdminUser {
  id: string;
  user_id: string;
  role: 'admin' | 'moderator';
  created_at: string;
  updated_at: string;
  created_by: string | null;
  user_name: string | null;
  user_email: string | null;
}

export const submitPostReport = async (
  postId: string,
  reason: string,
  description?: string
) => {
  const { data, error } = await supabase
    .from('post_reports')
    .insert({
      post_id: postId,
      reporter_id: (await supabase.auth.getUser()).data.user?.id,
      reason,
      description: description || null,
    })
    .select()
    .single();

  if (error) throw error;
  return data;
};

export const getReportsForModeration = async (): Promise<Report[]> => {
  const { data: user } = await supabase.auth.getUser();
  if (!user.user) throw new Error('Not authenticated');

  // Get basic report data first
  const { data: reportsData, error } = await supabase
    .from('post_reports')
    .select('*')
    .order('created_at', { ascending: false });

  if (error) throw error;
  if (!reportsData) return [];

  // Enrich with user and post data
  const enrichedReports = await Promise.all(
    reportsData.map(async (report) => {
      // Get reporter name
      const { data: reporter } = await supabase
        .from('users')
        .select('full_name')
        .eq('id', report.reporter_id)
        .maybeSingle();

      // Get post and pet info
      const { data: post } = await supabase
        .from('memory_posts')
        .select(`
          caption,
          pets(name)
        `)
        .eq('id', report.post_id)
        .maybeSingle();

      return {
        ...report,
        reporter_name: reporter?.full_name || null,
        post_caption: post?.caption || null,
        pet_name: (post as any)?.pets?.name || null,
      } as Report;
    })
  );

  return enrichedReports;
};

export const updateReportStatus = async (
  reportId: string,
  status: string,
  moderatorNotes?: string
) => {
  const { data: user } = await supabase.auth.getUser();
  if (!user.user) throw new Error('Not authenticated');

  const { data, error } = await supabase
    .from('post_reports')
    .update({
      status,
      moderator_notes: moderatorNotes || null,
      reviewed_by: user.user.id,
      reviewed_at: new Date().toISOString(),
      updated_at: new Date().toISOString(),
    })
    .eq('id', reportId)
    .select()
    .single();

  if (error) throw error;
  return data;
};

export const checkIfUserIsModerator = async () => {
  const { data: user } = await supabase.auth.getUser();
  if (!user.user) return false;

  try {
    // Use the database function to check moderator status
    const { data, error } = await supabase.rpc('check_user_is_moderator', {
      user_uuid: user.user.id
    });

    if (error) {
      console.error('Error checking moderator status:', error);
      return false;
    }

    return data === true;
  } catch (error) {
    console.error('Error checking moderator status:', error);
    return false;
  }
};

export const getUserRole = async () => {
  const { data: user } = await supabase.auth.getUser();
  if (!user.user) return null;

  try {
    const { data, error } = await supabase.rpc('get_user_role', {
      user_uuid: user.user.id
    });

    if (error) {
      console.error('Error getting user role:', error);
      return null;
    }

    return data;
  } catch (error) {
    console.error('Error getting user role:', error);
    return null;
  }
};

// Admin management functions
export const getAdminUsers = async (): Promise<AdminUser[]> => {
  const { data: adminData, error } = await supabase
    .from('admin_users')
    .select('*')
    .order('created_at', { ascending: false });

  if (error) throw error;
  if (!adminData) return [];

  // Enrich with user data
  const enrichedAdmins = await Promise.all(
    adminData.map(async (admin) => {
      const { data: userData } = await supabase
        .from('users')
        .select('full_name, email')
        .eq('id', admin.user_id)
        .maybeSingle();

      return {
        ...admin,
        user_name: userData?.full_name || null,
        user_email: userData?.email || null,
      } as AdminUser;
    })
  );

  return enrichedAdmins;
};

export const addAdminUser = async (
  userId: string,
  role: 'admin' | 'moderator'
) => {
  const { data: currentUser } = await supabase.auth.getUser();
  if (!currentUser.user) throw new Error('Not authenticated');

  const { data, error } = await supabase
    .from('admin_users')
    .insert({
      user_id: userId,
      role,
      created_by: currentUser.user.id,
    })
    .select()
    .single();

  if (error) throw error;
  return data;
};

export const updateAdminUser = async (
  adminId: string,
  role: 'admin' | 'moderator'
) => {
  const { data, error } = await supabase
    .from('admin_users')
    .update({
      role,
      updated_at: new Date().toISOString(),
    })
    .eq('id', adminId)
    .select()
    .single();

  if (error) throw error;
  return data;
};

export const removeAdminUser = async (adminId: string) => {
  const { error } = await supabase
    .from('admin_users')
    .delete()
    .eq('id', adminId);

  if (error) throw error;
};
